import api from "@/assets/js/utils/api";
import * as swal from "@/assets/js/utils/alertService";
import router from "@/router";

export default {
  name: "GoogleLoginCallback",
  data() {
    return {
      // 狀態管理
      isLoading: true,
      isSuccess: false,
      isError: false,

      // 設定文字
      pageTitle: "Google 登入處理",
      appName: "我的應用程式",
      processingMessage: "正在處理 Google 登入...",
      successMessage: "登入成功！歡迎使用",
      errorMessage: "登入失敗，請重試",
      continueButtonText: "繼續使用",

      // 回調資料
      callbackData: {
        code: null,
        state: null,
        scope: null,
        error: null,
        error_description: null,
      },

      // 用戶資訊
      userInfo: null,

      // 錯誤資料
      errorData: {
        code: "UNKNOWN",
        description: "請檢查您的網路連線或稍後再試",
      },

      // 授權時間
      authTime: "",

      // 授權範圍項目
      scopeItems: [],

      // 權限名稱對照表
      scopeNames: {
        openid: "基本身份驗證",
        email: "電子郵件地址",
        profile: "個人資料",
        "https://www.googleapis.com/auth/userinfo.email": "電子郵件地址",
        "https://www.googleapis.com/auth/userinfo.profile": "個人資料",
        "https://www.googleapis.com/auth/drive": "Google Drive 存取",
        "https://www.googleapis.com/auth/calendar": "Google 日曆",
        "https://www.googleapis.com/auth/gmail.readonly": "Gmail 讀取",
      },
    };
  },

  computed: {
    showActions() {
      return this.isSuccess || this.isError;
    },
  },

  mounted() {
    this.parseUrlParams();
    this.processCallback();

    // ✅ 清除網址參數，避免重新整理重複 callback
    window.history.replaceState({}, document.title, window.location.pathname);
  },

  methods: {
    // 解析 URL 參數
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);

      this.callbackData = {
        code: urlParams.get("code"),
        state: urlParams.get("state"),
        scope: urlParams.get("scope"),
        error: urlParams.get("error"),
        error_description: urlParams.get("error_description"),
      };

      console.log("Google callback 參數：", this.callbackData);
    },

    // ✅ 處理 Google 授權回調 - 修正版
    async processCallback() {
      const { code, state, error } = this.callbackData;

      try {
        // ⚠️ 有錯誤參數直接顯示錯誤
        if (error) {
          await this.handleError(
            "GOOGLE_ERROR",
            this.callbackData.error_description
          );
          return;
        }

        // ⚠️ 缺少必要參數
        if (!code || !state) {
          await this.handleError(
            "INVALID_REQUEST",
            "缺少必要參數 code 或 state"
          );
          return;
        }

        this.isLoading = true;

        // ⭐ 這裡決定你到底是在登入還是綁定
        const apiPath = state.startsWith("login_")
          ? "/front/oauth/google/login" // ⭐ Google 登入
          : "/oauth/google/callback"; // ⭐ Google 綁定

        // ⭐ 正確傳 query string （Axios 會自動接到 URL 上）
        const { data } = await api.get(apiPath, {
          code,
          state,
          scope: this.callbackData.scope,
        });

        console.log("Google callback 回傳：", data);
        this.isLoading = false;

        // ✅ 成功邏輯
        if (data.success) {
          await this.handleSuccess(data, state);
        }
        // ❌ 失敗邏輯
        else {
          await this.handleApiError(data, state);
        }
      } catch (err) {
        console.error("Google callback 發生錯誤：", err);
        await this.handleCatchError(err, state);
      }
    },

    // ✅ 處理成功情況
    async handleSuccess(data, state) {
      this.isSuccess = true;
      this.userInfo = data.data;
      this.authTime = new Date().toLocaleString("zh-TW");

      // 處理授權範圍
      if (this.callbackData.scope) {
        this.displayScopes(this.callbackData.scope);
      }

      // 🔹 通知父視窗成功
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "google-callback",
            success: true,
            message: state.startsWith("login_")
              ? "Google 登入成功，歡迎回來！"
              : data.message || "Google 帳號綁定成功！",
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // ✅ 主視窗模式才顯示
      if (state.startsWith("bind_")) {
        await swal.showSuccess(data.message || "Google 帳號綁定成功！");
        router.push("/dashboard/profile");
      } else if (state.startsWith("login_")) {
        await swal.showSuccess("登入成功，歡迎回來！");
        router.push("/dashboard");
      }
    },

    // ✅ 處理 API 回傳失敗
    async handleApiError(data, state) {
      this.isError = true;

      // 📌 偵測重複綁定錯誤
      const isDuplicateBinding =
        data?.code === 409 ||
        data?.message?.includes("已被綁定") ||
        data?.message?.includes("already bound");

      const errorMessage = isDuplicateBinding
        ? "此 Google 帳號已被綁定。"
        : data.message || "操作失敗，請稍後再試。";

      // 🔹 若有父視窗，通知父視窗
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "google-callback",
            success: false,
            message: errorMessage,
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // 🔹 主視窗：先顯示錯誤訊息再導頁
      await swal.showError(errorMessage);

      // 導向頁面
      if (state && state.startsWith("bind_")) {
        router.push("/dashboard/profile");
      } else {
        router.push("/login");
      }
    },

    // ✅ 處理 try-catch 錯誤
    async handleCatchError(err, state) {
      this.isLoading = false;
      this.isError = true;

      const backendMsg = err.response?.data?.message;
      const statusCode = err.response?.status;

      // 📌 偵測重複綁定錯誤
      const isDuplicateBinding =
        statusCode === 409 ||
        backendMsg?.includes("已被綁定") ||
        backendMsg?.includes("already bound");

      const errorMessage =
        backendMsg ||
        (isDuplicateBinding
          ? "此 Google 帳號已被綁定。"
          : "伺服器發生錯誤，請稍後再試。");

      // ✅ 有父視窗情況：統一交給父視窗顯示
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "google-callback",
            success: false,
            message: errorMessage,
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // ✅ 主視窗情況：必須顯示錯誤訊息
      await swal.showError(errorMessage);

      // 導頁
      if (state && state.startsWith("bind_")) {
        router.push("/dashboard/profile");
      } else {
        router.push("/login");
      }
    },

    // ✅ 統一的錯誤處理函數
    async handleError(errorCode, errorDesc) {
      this.isLoading = false;
      this.isError = true;

      const description = errorDesc || this.getErrorDescription(errorCode);

      this.errorData = {
        code: errorCode,
        description: description,
      };

      // 🔹 如有父視窗，通知父視窗
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "google-callback",
            success: false,
            message: description,
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // 🔹 主視窗：顯示錯誤訊息
      await swal.showError(description);
      router.push("/login");
    },

    // 處理授權範圍顯示
    displayScopes(scopeString) {
      const scopes = scopeString.split(" ");
      this.scopeItems = scopes.map((scope) => ({
        key: scope,
        name: this.scopeNames[scope] || scope,
      }));
    },

    // 顯示成功狀態
    showSuccess() {
      this.isLoading = false;
      this.isSuccess = true;

      // 模擬用戶資料
      this.userInfo = {
        name: "Google 用戶",
        email: "user@gmail.com",
        avatar: "G",
      };

      this.authTime = new Date().toLocaleString("zh-TW");

      // 處理授權範圍
      if (this.callbackData.scope) {
        this.displayScopes(this.callbackData.scope);
      }
    },

    // 顯示錯誤狀態
    showError(errorCode = null, errorDesc = null) {
      this.isLoading = false;
      this.isError = true;

      const code = errorCode || this.callbackData.error || "UNKNOWN_ERROR";
      const description =
        errorDesc ||
        this.callbackData.error_description ||
        this.getErrorDescription(code);

      this.errorData = {
        code: code,
        description: description,
      };
    },

    // 獲取錯誤描述
    getErrorDescription(errorCode) {
      const errorMessages = {
        access_denied: "用戶拒絕了授權請求",
        invalid_request: "請求參數無效或缺少必要參數",
        unauthorized_client: "客戶端未經授權執行此操作",
        unsupported_response_type: "不支援的回應類型",
        invalid_scope: "無效的授權範圍",
        server_error: "Google 伺服器發生內部錯誤",
        temporarily_unavailable: "Google 服務暫時無法使用",
        invalid_grant: "授權碼無效或已過期",
        redirect_uri_mismatch: "重定向 URI 不匹配",
        GOOGLE_ERROR: "Google 授權發生錯誤",
        INVALID_REQUEST: "請求無效",
      };

      return errorMessages[errorCode] || "發生未知錯誤，請稍後再試";
    },

    // 繼續到應用程式
    continueToApp() {
      this.$emit("login-success", {
        userInfo: this.userInfo,
        authData: this.callbackData,
        scopes: this.scopeItems,
      });
    },

    // 重試登入
    retryLogin() {
      this.$emit("retry-login");
    },
  },
};
